import React, { useCallback, useEffect, useState } from "react";
import { 
  Box, 
  IconButton, 
  Typography, 
  Tooltip 
} from "@mui/material";
import { 
  DataGrid, 
  GridColDef, 
  GridRenderCellParams 
} from "@mui/x-data-grid";
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
} from "@mui/icons-material";
import { confirmDialog } from "../../common/ConfirmDialog";
import { useNotifications } from "../../../hooks/NotificationProvider";
import SubjectModalForm from "./SubjectModalForm";
import SubjectModel, { SubjectData } from "../../../core/api/manager/SubjectModel";
import { translations } from "../../../translations/common";

interface ModalState {
  subjectId?: string | null;
  show: boolean;
}

const SubjectsTable: React.FC = () => {
  const [modal, setModal] = useState<ModalState>({ subjectId: null, show: false });
  const notifications = useNotifications();
  const [loading, setLoading] = useState<boolean>(true);
  const [subjects, setSubjects] = useState<SubjectData[]>([]);

  const loadSubjects = useCallback(() => {
    setLoading(true);
    SubjectModel.listAll()
      .then(data => {
        setSubjects(data);
        setLoading(false);
      })
      .catch(error => {
        console.error("Failed to load subjects:", error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadSubjects();
  }, [loadSubjects]);

  const handleAddSubject = () => {
    setModal({ show: true, subjectId: null });
  };

  const handleEditSubject = (subject: SubjectData) => {
    setModal({ show: true, subjectId: subject.id });
  };

  const handleDeleteSubject = (subject: SubjectData) => {
    confirmDialog({
      title: translations.subjects.table.confirmations.deleteTitle,
      body: translations.subjects.table.confirmations.deleteBody,
      action: async () => {
        if (subject.id) {
          try {
            await SubjectModel.delete(subject.id);
            loadSubjects();
            notifications.notify({
              level: "success",
              message: translations.subjects.table.notifications.deleteSuccess,
            });
          } catch (error) {
            console.error("Failed to delete subject:", error);
          }
        }
      },
      actionName: translations.subjects.table.confirmations.deleteAction,
    });
  };

  const onModalClose = () => {
    setModal({ show: false });
    loadSubjects();
  };

  const columns: GridColDef[] = [

    { 
      field: "subject", 
      headerName: translations.subjects.table.columns.subject, 
      flex: 1 
    },
    {
      field: "actions",
      headerName: "",
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<SubjectData>) => {
        const subject = params.row;
        return (
          <Box sx={{ display: "flex" }}>
            <Tooltip title={translations.subjects.table.actions.edit}>
              <IconButton 
                onClick={() => handleEditSubject(subject)} 
                size="small"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={translations.subjects.table.actions.delete}>
              <IconButton 
                onClick={() => handleDeleteSubject(subject)} 
                size="small"
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ 
      width: "100%", 
      height: '100%',
      bgcolor: 'background.paper',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f5f5f5', 
          fontWeight: 'bold',
          fontSize: '1.1rem',
          borderBottom: '1px solid #ddd'
        }}
      >
        <Typography variant="h6">{translations.subjects.table.title}</Typography>
        <Typography 
          variant="button" 
          sx={{ 
            color: 'primary.main',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
          onClick={handleAddSubject}
        >
          {translations.subjects.table.actions.add}
        </Typography>
      </Box>
      <DataGrid
        rows={subjects}
        columns={columns}
        loading={loading}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
        pageSizeOptions={[10]}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={(row) => row.id || `subject-${row.subject}`}
        getRowClassName={(params) => 
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
        }
        slots={{
          noRowsOverlay: () => (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              {translations.subjects.table.noData}
            </Box>
          ),
        }}
        sx={{
          border: 'none',
          '& .even-row': {
            backgroundColor: '#ffffff',
          },
          '& .odd-row': {
            backgroundColor: '#efefef',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#bdbdbd',
          },
          '& .hidden-column': {
            display: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
          },
          '& .MuiDataGrid-row': {
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
        }}
      />
      {modal.show && (
        <SubjectModalForm
          close={onModalClose}
          subjectId={modal.subjectId}
        />
      )}
    </Box>
  );
};

export default SubjectsTable;
